import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Mail, MapPin, Phone } from "lucide-react";
import { api, WHATSAPP_NUMBER, CONTACT_EMAIL, CONTACT_PHONE, CONTACT_ADDRESS, SOCIALS } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Subscribed! Wisdom soon in your inbox.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="relative mt-24 border-t border-[#D4AF37]/20 bg-[#0B1F3A] text-[#F5E8D8]">
      <div className="max-w-7xl mx-auto px-6 md:px-8 pt-16 pb-8 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <div className="mb-5">
            <img
              src="/assets/logo-full.png"
              alt="YourAstroHub - Vedic Astrology Consultation and Education Logo"
              className="h-32 md:h-36 w-auto drop-shadow-[0_0_25px_rgba(212,175,55,0.25)]"
              width="240" height="240"
              loading="lazy"
            />
            <div className="mt-3 text-[#D4AF37] italic font-serif text-lg">Your Guide to Happiness</div>
          </div>
          <p className="text-sm text-[#F5E8D8]/70 leading-relaxed">
            YourAstroHub is dedicated to providing authentic Vedic astrology consultations, practical remedies,
            and comprehensive astrology education to help individuals make informed life decisions.
          </p>
          <div className="flex gap-3 mt-5">
            <a data-testid="footer-instagram" href={SOCIALS.instagram} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full border border-[#D4AF37]/30 grid place-items-center hover:border-[#D4AF37] transition-colors"><Instagram className="w-4 h-4" /></a>
            <a data-testid="footer-facebook" href={SOCIALS.facebook} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full border border-[#D4AF37]/30 grid place-items-center hover:border-[#D4AF37] transition-colors"><Facebook className="w-4 h-4" /></a>
            <a data-testid="footer-youtube" href={SOCIALS.youtube} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full border border-[#D4AF37]/30 grid place-items-center hover:border-[#D4AF37] transition-colors"><Youtube className="w-4 h-4" /></a>
          </div>
        </div>

        <div>
          <div className="eyebrow mb-4">Explore</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-[#D4AF37]">About</Link></li>
            <li><Link to="/courses" className="hover:text-[#D4AF37]">Courses</Link></li>
            <li><Link to="/consultations" className="hover:text-[#D4AF37]">Consultation</Link></li>
            <li><Link to="/blog" className="hover:text-[#D4AF37]">Blog</Link></li>
            <li><Link to="/payment" className="hover:text-[#D4AF37]">Payment</Link></li>
            <li><Link to="/contact" className="hover:text-[#D4AF37]">Contact</Link></li>
          </ul>
        </div>

        <div>
          <div className="eyebrow mb-4">Contact</div>
          <ul className="space-y-3 text-sm text-[#F5E8D8]/80">
            <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 text-[#D4AF37]" /> <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></li>
            <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 text-[#D4AF37]" /> <a href={`tel:${CONTACT_PHONE}`}>{CONTACT_PHONE}</a></li>
            <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#D4AF37]" /> {CONTACT_ADDRESS}</li>
            <li>
              <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer" className="text-[#D4AF37] hover:underline">Chat on WhatsApp</a>
            </li>
          </ul>
        </div>

        <div>
          <div className="eyebrow mb-4">Newsletter</div>
          <p className="text-sm text-[#F5E8D8]/70 mb-3">Monthly wisdom, remedies and course updates. No noise.</p>
          <form onSubmit={submit} className="flex flex-col gap-2">
            <Input
              type="email"
              data-testid="newsletter-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="bg-[#0F284B] border-[#D4AF37]/25 text-[#F5E8D8] placeholder:text-[#F5E8D8]/40 rounded-full px-4"
              required
            />
            <Button
              data-testid="newsletter-submit"
              disabled={busy}
              className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold"
            >
              {busy ? "Subscribing..." : "Subscribe"}
            </Button>
          </form>
        </div>
      </div>

      <div className="border-t border-[#D4AF37]/15 py-6 px-6 max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#F5E8D8]/60">
        <div>© {new Date().getFullYear()} YourAstroHub. All rights reserved.</div>
        <div className="flex flex-wrap items-center gap-4">
          <Link to="/privacy" className="hover:text-[#D4AF37]">Privacy</Link>
          <Link to="/terms" className="hover:text-[#D4AF37]">Terms</Link>
          <Link to="/refund-policy" className="hover:text-[#D4AF37]">Refund</Link>
          <Link to="/disclaimer" className="hover:text-[#D4AF37]">Disclaimer</Link>
        </div>
        <div>Decode Your Destiny with Vedic Wisdom.</div>
      </div>
    </footer>
  );
}
